<template>
  <div
    v-lazy-load="shouldUseLazyLoad"
    class="mix-and-match"
    :class="{
      selected: isMixAndMatchOptionSelected(categoryKey, option.id),
    }"
  >
    <div
      v-if="visible"
      class="mix-and-match-container"
      @click="setMixAndMatchOption(categoryKey, option)"
      :tabindex="tabNavigationEnabled ? 0 : null"
      @keyup.enter="setMixAndMatchOption(categoryKey, option)"
    >
      <RemoveIcon v-if="option.id === 0" />
      <img
        v-else
        class="mix-and-match-trait"
        :style="mixAndMatchTraitStyle"
        :src="getOptionPreview(categoryKey, option)"
        @load="onLoadEnd"
        @error="onError"
        draggable="false"
      />
    </div>
    <span v-if="isOptionPromoted" class="badge-dot" />
  </div>
</template>

<script>
import { OutfitActionTypeEnum } from '@/enum/outfit_action_type';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import {
  MixAndMatchCategoryEnum,
  MixAndMatchUnderwearEnum,
  MixAndMatchTuckStateEnum,
  MixAndMatchDefaultTuckStateEnum,
} from '@/enum/mix_and_match';

import Blizzard from '@/services/Blizzard';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import RemoveIcon from '@/components/AvatarBuilderUI/TraitPreviews/RemoveIcon';
import { BackgroundTypeEnum } from '@/enum/background';

export default {
  name: 'MixAndMatchWithSectionTraitPreview',
  props: [
    'option',
    'itemStyle',
    'categoryKey',
    'brandId',
    'brandBackground',
    'categoryDetails',
    'tabNavigationEnabled',
  ],
  components: {
    RemoveIcon,
  },
  data() {
    return {
      visible: false, // Managed by LazyLoad
      observer: null, // Managed by LazyLoad
    };
  },
  methods: {
    onLoadEnd() {
      Blizzard.recordTraitPreviewLoad(false);
    },
    onError() {
      Blizzard.recordTraitPreviewLoad(true);
    },
    isMixAndMatchOptionSelected(category, value) {
      const itemCategory = MixAndMatchHelper.getItemCategory(category);
      const isWearingUnderwear = [
        MixAndMatchUnderwearEnum.UNDERWEAR_TOP,
        MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM,
      ].includes(this.$store.getters.traits[itemCategory]);

      if (
        (this.$store.getters.traits[itemCategory] === value ||
          (isWearingUnderwear && value === 0)) &&
        this.$store.getters.traits.clothing_type === OutfitTypeEnum.SPLIT
      ) {
        return true;
      }

      // Also select remove icon when wearing a bodysuit bottom
      const currentBodysuit = MixAndMatchHelper.getCurrentBodysuit(
        this.$store.getters.traits.top,
        this.$store.getters.categories,
      );
      const isWearingBodysuitBottom =
        currentBodysuit && currentBodysuit.id === this.$store.getters.traits[itemCategory];
      return (
        isWearingBodysuitBottom &&
        itemCategory === MixAndMatchCategoryEnum.BOTTOM &&
        value === 0 &&
        this.$store.getters.traits.clothing_type === OutfitTypeEnum.SPLIT
      );
    },
    getOptionPreview(category, item) {
      const isFullPiece =
        item.is_bodysuit || item.is_one_piece || category === MixAndMatchCategoryEnum.DRESS;
      const currentCategory = MixAndMatchHelper.getItemCategory(category);

      // NOTE: The preview image may be inconsistent with current colours in undo/redo scenario
      // due to inconsistency between values in "getters.traits" and
      // "getters.selectedColourPalettesForMixAndMatchItems" TODO: FIX!
      const values = this.mixAndMatchOptionTraitsDictionary(
        currentCategory,
        item.id,
        item.color_group_ids,
        item.default_color_info,
      );

      return this.$store.getters.getMixAndMatchOptionPreview({
        category: category,
        values,
        isFullPiece,
      });
    },
    setMixAndMatchOption(category, item) {
      // consider logic to put on dress like putting on top
      const itemCategory = MixAndMatchHelper.getItemCategory(category);
      const initialTraits = Object.assign({}, this.$store.getters.traits);
      const currentBodysuit = MixAndMatchHelper.getCurrentBodysuit(
        this.$store.getters.traits.top,
        this.$store.getters.categories,
      );
      const isWearingFullBodysuit = MixAndMatchHelper.isWearingFullBodysuit(
        this.$store.getters.traits.bottom,
        this.$store.getters.traits.top,
        this.$store.getters.categories,
      );
      this.isOptionPromoted &&
        this.$store.commit('clearStaleOptionPromotion', { category: category, optionId: item.id });
      // Special case when taking off bottom (putting on id: 0) while wearing bodysuit top
      // Check if bodysuit bottom already been put on so won't be putting on the bottom twice
      const isRemovingBottom = category === MixAndMatchCategoryEnum.BOTTOM && item.id === 0;
      const bodysuitOnWhenRemoveBottom = isRemovingBottom && currentBodysuit;
      const bottomHasNoRise = !this.$store.getters.mixAndMatchMetadata.bottoms_tuckable[item.id];

      this.handleTucking(item, itemCategory, currentBodysuit, bottomHasNoRise);

      // All bodysuit and one pieces cases only affect tops and bottoms, don't handle it otherwise
      if (
        category === MixAndMatchCategoryEnum.TOP ||
        category === MixAndMatchCategoryEnum.BOTTOM ||
        category === MixAndMatchCategoryEnum.DRESS
      ) {
        this.handleSetBodysuitBottomIfNeeded(
          bodysuitOnWhenRemoveBottom,
          itemCategory,
          item,
          currentBodysuit,
          isWearingFullBodysuit,
        );

        // Special case: If putting on a no rise bottom (e.g. swim bottom) while wearing a full bodysuit,
        // Automatically remove the top
        if (bottomHasNoRise && currentBodysuit && !isRemovingBottom) {
          this.$store.commit('setMixAndMatchItem', {
            category: MixAndMatchCategoryEnum.TOP,
            value: {
              [MixAndMatchCategoryEnum.TOP]: 0,
            },
          });
        }

        // Handle taking off one pieces (dresses/jumpsuits)
        // Expected behaviour: if putting on a top or bottom while wearing a one piece,
        // Take off the one piece completely and put on an underwear
        if (
          this.$store.getters.mixAndMatchMetadata.one_pieces[this.$store.getters.traits.top] &&
          !item.is_one_piece &&
          !item.is_bodysuit
        ) {
          if (category === MixAndMatchCategoryEnum.TOP) {
            this.$store.commit('setMixAndMatchItem', {
              category: MixAndMatchCategoryEnum.BOTTOM,
              value: {
                [MixAndMatchCategoryEnum.BOTTOM]: 0,
              },
            });
          }
          if (category === MixAndMatchCategoryEnum.BOTTOM) {
            this.$store.commit('setMixAndMatchItem', {
              category: MixAndMatchCategoryEnum.TOP,
              value: {
                [MixAndMatchCategoryEnum.TOP]: 0,
              },
            });
          }
        }
      }

      // Handle one pieces (dresses/jumpsuits)
      if (
        (category === MixAndMatchCategoryEnum.TOP && item.is_one_piece) ||
        category === MixAndMatchCategoryEnum.DRESS
      ) {
        // Put on bottom part of the one piece
        this.$store.commit('setMixAndMatchItem', {
          category: MixAndMatchCategoryEnum.BOTTOM,
          value: this.mixAndMatchOptionTraitsDictionary(
            MixAndMatchCategoryEnum.BOTTOM,
            item.id,
            item.color_group_ids,
            item.default_color_info,
            true,
          ),
        });
      }

      if (!bodysuitOnWhenRemoveBottom) {
        // Put on a regular garment
        this.$store.commit('setMixAndMatchItem', {
          category: itemCategory,
          value: this.mixAndMatchOptionTraitsDictionary(
            itemCategory,
            item.id,
            item.color_group_ids,
            item.default_color_info,
            false,
          ),
        });
      }

      // When select a default item, set garment's default colour to mix and match colour palettes
      if (item.id !== 0 && this.isColourPaletteForItemAbsent(itemCategory, item.id)) {
        this.setDefaultColourToMixAndMatchItem(
          itemCategory,
          item.id,
          item.default_color_info,
          false,
        );

        // When select a default bodysuit, also set bodysuit bottom's default colour to mix and match colour palettes
        // Check if wearing pants, because if not wearing pants, handleSetBodysuitBottomIfNeeded will already handled this
        if (
          item.is_bodysuit &&
          this.$store.getters.traits.bottom !== MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM
        ) {
          this.setDefaultColourToMixAndMatchItem(
            MixAndMatchCategoryEnum.BOTTOM,
            item.id,
            item.default_color_info,
            true,
          );
        }
      }

      // undo redo actions
      const initialBackground = {
        backgroundId: this.$store.getters.backgroundId,
        backgroundUrl: this.$store.getters.backgroundUrl,
        backgroundType: this.$store.getters.backgroundType,
      };
      this.$store.commit('updateAvatarActions', {
        initialTraits,
        initialBackground,
      });

      // Set background
      if (
        this.$store.getters.backgroundId !== this.brandId &&
        this.$store.getters.backgroundUrl !== this.brandBackground
      ) {
        const brandId = this.brandBackground === '' ? null : this.brandId;
        this.$store.commit('setBackgroundId', {
          id: brandId,
        });
        this.$store.commit('setBackgroundUrl', {
          url: this.brandBackground,
        });
        this.$store.commit('setBackgroundType', {
          type: BackgroundTypeEnum.MIX_AND_MATCH,
        });
      }

      // Log metrics
      this.$store.commit('logOutfitAction', {
        action: OutfitActionTypeEnum.TAP_MIX_AND_MATCH_ITEM,
      });
    },
    handleTucking(item, category, currentBodysuit, bottomHasNoRise) {
      if (category === MixAndMatchCategoryEnum.TOP) {
        const tuckStateBoolean =
          (item.default_tuck_state === MixAndMatchDefaultTuckStateEnum.TUCK &&
            this.$store.getters.traits.bottom !== MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM) ||
          item.is_bodysuit ||
          (item.default_tuck_state !== MixAndMatchDefaultTuckStateEnum.NONE &&
            this.$store.getters.traits.bottom !== MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM &&
            item.id in this.$store.getters.tuckStateForTop &&
            this.$store.getters.tuckStateForTop[item.id])
            ? true
            : false;
        const tuckStateEnum = tuckStateBoolean
          ? MixAndMatchTuckStateEnum.TUCK
          : MixAndMatchTuckStateEnum.UNTUCK;
        const isTopTuckable =
          item.id === 0
            ? false
            : item.is_bodysuit || item.default_tuck_state !== MixAndMatchDefaultTuckStateEnum.NONE;

        this.$store.commit('setTuckForMixAndMatchTop', {
          tuckState: tuckStateEnum,
          isTopTuckable,
        });
        this.$store.commit('setTopToggleTuckState', { tuckState: tuckStateBoolean });

        const isDisabled =
          !item.default_tuck_state ||
          item.default_tuck_state === MixAndMatchDefaultTuckStateEnum.NONE ||
          this.$store.getters.traits.bottom === MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM;

        this.$store.commit('setIsTopToggleDisabled', { isDisabled });
      }

      if (category === MixAndMatchCategoryEnum.BOTTOM) {
        // Disable the toggle if
        // 1) wearing underwear or
        // 2) currently wearing a bodysuit
        // 3) putting on no rise pants
        const currentTopId = this.$store.getters.traits.top;
        const gender = this.$store.getters.gender;
        const topIsTuckable =
          this.$store.getters.mixAndMatchMetadata.tops_default_tuck_state[currentTopId] &&
          this.$store.getters.mixAndMatchMetadata.tops_default_tuck_state[currentTopId].tuck_state[
            gender
          ] !== MixAndMatchDefaultTuckStateEnum.NONE;
        const isDisabled =
          item.id === 0 ||
          (item.id !== 0 && currentBodysuit) ||
          this.$store.getters.traits.top === 0 ||
          !topIsTuckable ||
          bottomHasNoRise
            ? true
            : false;
        // If 1) taking off bottoms and not wearing a bodysuit or
        // 2) putting on no rise pants,
        // untuck clothing
        if (item.id === 0 || bottomHasNoRise) {
          this.$store.commit('setTuckForMixAndMatchTop', {
            tuckState: MixAndMatchTuckStateEnum.UNTUCK,
            isTopTuckable: topIsTuckable,
          });
          this.$store.commit('setTopToggleTuckState', { tuckState: false });
          //  if putting bottoms back on while wearing a bodysuit and bodysuit isn't currently tucked, tuck it
        } else if (
          (currentBodysuit &&
            this.$store.getters.traits.is_tucked !== MixAndMatchTuckStateEnum.TUCK) ||
          (topIsTuckable && this.$store.getters.tuckStateForTop[this.$store.getters.traits.top])
        ) {
          this.$store.commit('setTuckForMixAndMatchTop', {
            tuckState: MixAndMatchTuckStateEnum.TUCK,
            isTopTuckable: true,
          });
          this.$store.commit('setTopToggleTuckState', { tuckState: true });
        }
        this.$store.commit('setIsTopToggleDisabled', { isDisabled });
      }
    },
    handleSetBodysuitBottomIfNeeded(
      bodysuitOnWhenRemoveBottom,
      category,
      itemToPutOn,
      currentBodysuit,
      isWearingFullBodysuit,
    ) {
      const notWearingBottom =
        this.$store.getters.traits.bottom === MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM ||
        this.$store.getters.traits.clothing_type !== OutfitTypeEnum.SPLIT;
      const colourGroupIds =
        itemToPutOn.id === 0 && currentBodysuit
          ? currentBodysuit.color_group_ids
          : itemToPutOn.color_group_ids;
      const isWearingNoRiseBottom = !this.$store.getters.mixAndMatchMetadata.bottoms_tuckable[
        this.$store.getters.traits.bottom
      ];
      // Bodysuit special case: put on bodysuit bottom if take off pants and wearing a bodysuit
      if (bodysuitOnWhenRemoveBottom) {
        this.$store.commit('setMixAndMatchItem', {
          category: MixAndMatchCategoryEnum.BOTTOM,
          value: this.mixAndMatchOptionTraitsDictionary(
            MixAndMatchCategoryEnum.BOTTOM,
            currentBodysuit.id,
            colourGroupIds,
            null,
            true,
          ),
        });

        // Also remove bodysuit tucked state
        this.$store.commit('setTuckForMixAndMatchTop', {
          tuckState: MixAndMatchTuckStateEnum.UNTUCK,
          isTopTuckable: true,
        });
        this.$store.commit('setTopToggleTuckState', { tuckState: false });
      }
      // Bodysuit special case: if full bodysuit is on, and choose other non-bodysuit top, take off full bodysuit
      else if (
        category === MixAndMatchCategoryEnum.TOP &&
        isWearingFullBodysuit &&
        !itemToPutOn.is_bodysuit
      ) {
        this.$store.commit('setMixAndMatchItem', {
          category: MixAndMatchCategoryEnum.BOTTOM,
          value: this.mixAndMatchOptionTraitsDictionary(MixAndMatchCategoryEnum.BOTTOM, 0, []),
        });
      }
      // Bodysuit special case: put on full bodysuits if either
      // 1) not wearing pants
      // 2) currently wearing another full bodysuit
      // 3) wearing no rise pants
      else if (
        itemToPutOn.is_bodysuit &&
        (notWearingBottom || isWearingFullBodysuit || isWearingNoRiseBottom)
      ) {
        this.$store.commit('setMixAndMatchItem', {
          category: MixAndMatchCategoryEnum.BOTTOM,
          value: this.mixAndMatchOptionTraitsDictionary(
            MixAndMatchCategoryEnum.BOTTOM,
            itemToPutOn.id,
            colourGroupIds,
            itemToPutOn.default_color_info,
            true,
          ),
        });
        // When select a default bodysuit, set bodysuit bottom colour to mix and match colour palettes
        if (this.isColourPaletteForItemAbsent(category, itemToPutOn.id)) {
          this.setDefaultColourToMixAndMatchItem(
            MixAndMatchCategoryEnum.BOTTOM,
            itemToPutOn.id,
            itemToPutOn.default_color_info,
            true,
          );
        }
        // Also remove tuck state
        this.$store.commit('setTuckForMixAndMatchTop', {
          tuckState: MixAndMatchTuckStateEnum.UNTUCK,
          isTopTuckable: true,
        });
        this.$store.commit('setTopToggleTuckState', { tuckState: false });
      }
    },
    mixAndMatchOptionTraitsDictionary(
      category,
      item,
      colourGroupIds,
      defaultColour,
      withBodysuitBottom,
    ) {
      const selectedPalette = this.$store.getters.selectedColourPalettesForMixAndMatchItems[
        category
      ]
        ? this.$store.getters.selectedColourPalettesForMixAndMatchItems[category][item]
        : null;
      let defaultPalette;
      if (colourGroupIds.length > 0) {
        // The default palette of the garment is the swatch_set_index index in the group_id colour group.
        const defaultColourGroupId = defaultColour ? defaultColour.group_id : colourGroupIds[0];
        const defaultColourGroup = this.$store.getters.mixAndMatchColourGroups[
          defaultColourGroupId
        ];

        if (defaultColourGroup && defaultColourGroup.tones && defaultColourGroup.tones.length > 0) {
          defaultPalette = defaultColour
            ? defaultColourGroup.tones[defaultColour.swatch_set_index]
            : defaultColourGroup.tones[0];
        }

        if (withBodysuitBottom) {
          // Set the bodysuit bottom colour palette to match the bodysuit top colour palette.
          defaultPalette = MixAndMatchHelper.replacePaletteTopWithBottomKey(defaultPalette);
        }
      }

      const colourPalette = selectedPalette ? selectedPalette : defaultPalette;

      return Object.assign({ [category]: item }, colourPalette);
    },
    setDefaultColourToMixAndMatchItem(category, value, defaultColourInfo, handleBodysuitBottom) {
      // For garments that don't have any colour group set, check if the group_id exist first
      const defaultColourGroup = this.$store.getters.mixAndMatchColourGroups[
        defaultColourInfo.group_id
      ];
      let defaultColour = {};
      if (defaultColourGroup && defaultColourGroup.tones && defaultColourGroup.tones.length > 0) {
        defaultColour = defaultColourGroup.tones[defaultColourInfo.swatch_set_index];
      }

      if (handleBodysuitBottom) {
        defaultColour = MixAndMatchHelper.replacePaletteTopWithBottomKey(defaultColour);
      }

      this.$store.commit('setSelectedColourPalettesForMixAndMatchItems', {
        category: category,
        itemId: value,
        colourPalette: defaultColour,
      });
    },
    isColourPaletteForItemAbsent(category, value) {
      return !(
        this.$store.getters.selectedColourPalettesForMixAndMatchItems[category] &&
        this.$store.getters.selectedColourPalettesForMixAndMatchItems[category][value]
      );
    },
    isDefaultTucked(item) {
      return item.is_bodysuit || item.default_tuck_state === MixAndMatchDefaultTuckStateEnum.TUCK;
    },
  },
  computed: {
    shouldUseLazyLoad() {
      const numRowsAlwaysShow = 4;
      const numOptionsAlwaysShow = this.categoryDetails.column_count * numRowsAlwaysShow;

      return this.option.index >= numOptionsAlwaysShow;
    },
    mixAndMatchTraitStyle: function() {
      if (
        this.categoryKey === MixAndMatchCategoryEnum.TOP ||
        this.categoryKey === MixAndMatchCategoryEnum.OUTERWEAR
      ) {
        return {
          maxWidth: '70%',
        };
      } else if (
        this.categoryKey === MixAndMatchCategoryEnum.FOOTWEAR ||
        this.categoryKey === MixAndMatchCategoryEnum.SOCK
      ) {
        return {
          maxWidth: '75%',
        };
      }

      return {
        maxWidth: '60%',
      };
    },
    isOptionPromoted() {
      return this.$store.getters.isOptionPromoted(this.categoryKey, this.option.id);
    },
  },
};
</script>

<style scoped>
.mix-and-match-container {
  min-height: 96px;
  width: 100%;
  height: 100%;
  padding: 10%;
  box-sizing: border-box;
  display: flex;
  justify-content: center; /* horizontally */
  align-items: center; /* vertically */
}

.mix-and-match-trait {
  max-height: 100%;
}

.mix-and-match:active {
  transform: scale(0.9);
  transition-duration: 0.1s;
}

.mix-and-match {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #f9fbfc;
  /* Use relative so that child elements that are absolutely positioned render within this div */
  position: relative;
}

.mix-and-match.selected {
  background-color: #e6f6ff;
  border-radius: 10px;
  box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #ffffff;
  -webkit-box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #ffffff;
}

@media (hover: hover) {
  .mix-and-match:hover {
    box-shadow: inset 0 0 0 2px rgba(217, 217, 217, 0.17);
  }
}

@media (prefers-color-scheme: dark) {
  .mix-and-match {
    background-color: #1e1e1e;
  }
  .mix-and-match.selected {
    box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #121212;
    -webkit-box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #121212;
    background-color: #1f1f1f;
  }
}

.badge-dot {
  height: 8px;
  width: 8px;
  background-color: #0a92ff;
  border-radius: 50%;
  position: absolute;
  top: 4px;
  right: 4px;
  margin: 0;
}
</style>
